/**
 * Централизованная конфигурация лимитов тарифных планов
 * 
 * ВНИМАНИЕ: Это ЕДИНСТВЕННОЕ место для определения лимитов!
 * Изменение лимитов в этом файле автоматически применяется к:
 * - Cloud Functions (functions/src/index.ts)
 * - Frontend (src/lib/plans.ts)
 * 
 * Типы планов:
 * - free: Базовый план для ознакомления
 * - pro: Расширенный план для активной работы
 * - team: План для командной работы
 */

/**
 * Тип плана пользователя
 */
export type PlanType = 'free' | 'pro' | 'team';

/**
 * Интерфейс лимитов одного плана
 */
export interface PlanLimitConfig {
  maxProjects: number;      // Максимальное количество обычных проектов
  maxVersions: number;      // Максимальное количество версий на проект
  maxScreens: number;       // Максимальное количество экранов на версию
  maxEvents: number;        // Максимальное количество событий на проект
  maxMembers: number;       // Максимальное количество участников в проекте
  maxDemoProjects: number;  // Максимальное количество демо-проектов (всегда 3)
}

/**
 * Словарь лимитов по типам планов
 * Единый источник истины для всех лимитов
 */
export const PLAN_LIMITS: Record<PlanType, PlanLimitConfig> = {
  free: {
    maxProjects: 3,
    maxVersions: 3,
    maxScreens: 10,
    maxEvents: 200,
    maxMembers: 3,
    maxDemoProjects: 3,
  },
  pro: {
    maxProjects: 15,
    maxVersions: 10,
    maxScreens: 50,
    maxEvents: 1000,
    maxMembers: 10,
    maxDemoProjects: 3,
  },
  team: {
    maxProjects: 50,
    maxVersions: 25,
    maxScreens: 100,
    maxEvents: 10000,
    maxMembers: 30,
    maxDemoProjects: 3,
  },
} as const;

/**
 * Тип для указания плана
 */
export type PlanKey = keyof typeof PLAN_LIMITS;

/**
 * Получить лимиты для указанного плана
 * @param plan - тип плана
 * @returns объект лимитов для указанного плана
 */
export function getPlanLimits(plan: PlanType): PlanLimitConfig {
  return PLAN_LIMITS[plan] || PLAN_LIMITS.free;
}

/**
 * Проверить, является ли план допустимым
 */
export function isValidPlan(plan: string): plan is PlanType {
  return plan in PLAN_LIMITS;
}

/**
 * Получить все доступные планы
 */
export function getAllPlans(): Array<{ type: PlanType; limits: PlanLimitConfig }> {
  return (Object.keys(PLAN_LIMITS) as PlanType[]).map(plan => ({
    type: plan,
    limits: PLAN_LIMITS[plan],
  }));
}

export default PLAN_LIMITS;
